import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  AfterViewInit,
  Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject, BehaviorSubject, combineLatest, Observable } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  first,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { AngularYandexMapsModule, YaReadyEvent } from 'angular8-yandex-maps';
import { NzSelectModule, NzSelectOptionInterface } from 'ng-zorro-antd/select';
import { CenterModel } from '@medindex-webapi';
import orderBy from 'lodash-es/orderBy';

import {
  mapPanOptions,
  optionsMap,
  placemarkCenterOptions,
  placemarkCenterOptionsLite,
  placemarkCenterSelectedOptions,
  stateMap,
} from '@shared/modules/map/constants/optionsMap';
import { MapService } from '@shared/modules/map/map.service';
import { ErrorMapService } from '@shared/modules/map/services/error-map.service';
import { DeclensionService } from '@shared/services/declension.service';
import { BalloonMobileModule } from '@shared/modules/map/modules/balloon-mobile/balloon-mobile.module';
import { IPlacemarkCenter } from './types/promotion-map.types';
import { PromotionMapService } from './services/promotion-map.service';
import { NavigationMenuService } from '@shared/services/navigation-menu.service';

import markplace from '!!file-loader!../../../../shared/modules/map/assets/mark.svg';

@Component({
  selector: 'app-promotion-map',
  templateUrl: './promotion-map.component.html',
  styleUrls: ['./promotion-map.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    NzSelectModule,
    AngularYandexMapsModule,
    BalloonMobileModule,
  ],
  providers: [PromotionMapService, MapService, DeclensionService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromotionMapComponent implements AfterViewInit, OnDestroy {
  private _centers = new BehaviorSubject<CenterModel[]>([]);
  @Input() set centers(value: CenterModel[]) {
    this._centers.next(value);
  }

  selectedMetro$ = new BehaviorSubject<string | null>(null);

  metroStationOptions$ = this._centers.pipe(
    map((centers) => {
      const metroOptionsMap = new Map<string, NzSelectOptionInterface>();
      for (const center of centers) {
        for (const subwayStation of center.subwayStations ?? []) {
          if (subwayStation.code)
            metroOptionsMap.set(subwayStation.code, {
              value: subwayStation.code,
              label: subwayStation.name,
            });
        }
      }
      return orderBy([...metroOptionsMap.values()], (o) => o.label);
    })
  );

  private filteredCenters$ = combineLatest([
    this.selectedMetro$,
    this._centers,
  ]).pipe(
    map(([selectedMetro, centers]) =>
      centers.filter(
        (center) =>
          !selectedMetro ||
          center.subwayStations?.some(
            (station) => station.code === selectedMetro
          )
      )
    )
  );

  private isHelixLite$: Observable<boolean> =
    this.navigationMenu.isHelixLiteAvailable$;

  markplace = markplace;

  stateMap = stateMap;
  optionsMap = optionsMap;
  clustererOptions: ymaps.IClustererOptions = {};

  private readonly _isMapReady$ = new BehaviorSubject<boolean>(false);
  private isMapReady$ = this._isMapReady$.pipe(
    distinctUntilChanged(),
    filter((isReady) => isReady)
  );

  protected readonly placemarksCenters$ = this.isMapReady$.pipe(
    switchMap(() => combineLatest([this.filteredCenters$, this.isHelixLite$])),
    filter(([_, isHelixLite]) => isHelixLite !== null),
    map(([centers, isHelixLite]) =>
      this.promotionMapService.generatePlacemarksCenters(centers, isHelixLite)
    )
  );

  selectedPlacemark: IPlacemarkCenter | null = null;

  isOpenBalloonMobile = false;

  ngUnsubscribe$ = new Subject<void>();

  constructor(
    public promotionMapService: PromotionMapService,
    private errorMapService: ErrorMapService,
    private router: Router,
    private navigationMenu: NavigationMenuService
  ) {}

  onNavigate(element: EventTarget | null) {
    const centerInfoCode = (element as HTMLElement).getAttribute(
      'centerInfoCode'
    );
    if (centerInfoCode) {
      this.router.navigate([`/center/${centerInfoCode}`]);
    }
  }

  ngAfterViewInit() {
    this.errorMapService.handler();
  }

  selectMetro(code: string | null): void {
    this.selectedMetro$.next(code);
  }

  onMapReady(yaMap: YaReadyEvent): void {
    yaMap.target.controls.remove('fullscreenControl');
    this._isMapReady$.next(true);
    this.clustererOptions = this.promotionMapService.getClustererOptions(ymaps);

    this.panToPlacemarkCenters(yaMap);
  }

  private panToPlacemarkCenters(yaMap: YaReadyEvent): void {
    this.placemarksCenters$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((centers) => {
        if (centers.length) {
          const centerMap = this.promotionMapService.getCenterMap(centers);

          yaMap.target.panTo(centerMap, mapPanOptions);
        }
      });
  }

  selectCenter(center: IPlacemarkCenter): void {
    this.resetOptionsPlacemark();

    this.selectedPlacemark = center;

    if (this.selectedPlacemark && this.promotionMapService.isMobile) {
      this.selectedPlacemark.options = placemarkCenterSelectedOptions;
      this.selectedPlacemark.selected = true;

      this.isOpenBalloonMobile = true;
    }
  }

  closeBalloonMobile(): void {
    this.isOpenBalloonMobile = false;

    this.resetOptionsPlacemark();
  }

  resetMap(yaMap: YaReadyEvent): void {
    if (yaMap.target.balloon.isOpen()) {
      yaMap.target.balloon.close();
    }

    this.placemarksCenters$.pipe(first()).subscribe((placemarksCenters) =>
      placemarksCenters.forEach((placemark) => {
        placemark.options = { hasBalloon: !this.promotionMapService.isMobile };
      })
    );

    if (this.isOpenBalloonMobile) this.isOpenBalloonMobile = false;

    this.resetOptionsPlacemark();
  }

  resetOptionsPlacemark(): void {
    this.isHelixLite$.pipe(first()).subscribe((isHelixLite) => {
      if (!this.selectedPlacemark) {
        return;
      }
      const placemarkOptions = isHelixLite
        ? placemarkCenterOptionsLite
        : placemarkCenterOptions;

      this.selectedPlacemark.options = {
        ...placemarkOptions,
        hasBalloon: !this.promotionMapService.isMobile,
      };
      this.selectedPlacemark.selected = false;
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
